// components/LaborCatalogTable.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

export default function LaborCatalogTable({ 
    items, 
    totalCount, 
    loading, 
    lazyParams, 
    onPage, 
    sortField,
    sortOrder,
    onSortChange,
    activeFilters,
    onFiltersChange,
}) {
    const { th } = useLanguage();

    const [hasYurticiColumn, setHasYurticiColumn] = useState(false);
    const [hasYurtdisiColumn, setHasYurtdisiColumn] = useState(false);


    useEffect(() => {
        if (items.length > 0) {
            const hasYurtici = items.some(item => item.yurticiSipMkt != null);
            const hasYurtdisi = items.some(item => item.yurtdisiSipMkt != null);
            
            if (hasYurtici) setHasYurticiColumn(true);
            if (hasYurtdisi) setHasYurtdisiColumn(true);
        }
    }, [items]);

  
    const hasYurticiData = hasYurticiColumn;
    const hasYurtdisiData = hasYurtdisiColumn;


    const formatDecimal = (value) => {
        if (value == null) return '-';
        return Number(value).toLocaleString('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const rows = Array.isArray(items)
        ? items.map((item, index) => ({ ...item, _rowId: `${lazyParams.first}-${index}` }))
        : [];

    const handleSort = (e) => {
        onSortChange?.(e);
    };

    const tableFilters = useMemo(
        () => ({
            matnr: {
                value: activeFilters?.matnr ?? null,
                matchMode: activeFilters?.matnrMatchMode ?? FilterMatchMode.CONTAINS
            },
            laborDescription: {
                value: activeFilters?.laborDescription ?? null,
                matchMode: activeFilters?.laborDescriptionMatchMode ?? FilterMatchMode.CONTAINS
            },
            meins: {
                value: activeFilters?.meins ?? null,
                matchMode: activeFilters?.meinsMatchMode ?? FilterMatchMode.CONTAINS
            },
            ...(hasYurticiData && {
                yurticiSipMkt: {
                    value: activeFilters?.yurticiSipMkt ?? null,
                    matchMode: activeFilters?.yurticiSipMktMatchMode ?? FilterMatchMode.CONTAINS
                }
            }),
            ...(hasYurtdisiData && {
                yurtdisiSipMkt: {
                    value: activeFilters?.yurtdisiSipMkt ?? null,
                    matchMode: activeFilters?.yurtdisiSipMktMatchMode ?? FilterMatchMode.CONTAINS
                }
            }),
        }),
        [activeFilters, hasYurticiData, hasYurtdisiData]
    );

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};

        const updatedFilters = {
            ...(activeFilters || {}),
            matnr: 'matnr' in filters ? filters.matnr?.value ?? null : activeFilters?.matnr ?? null,
            matnrMatchMode: 'matnr' in filters ? filters.matnr?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.matnrMatchMode ?? FilterMatchMode.CONTAINS,
            laborDescription: 'laborDescription' in filters ? filters.laborDescription?.value ?? null : activeFilters?.laborDescription ?? null,
            laborDescriptionMatchMode: 'laborDescription' in filters ? filters.laborDescription?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.laborDescriptionMatchMode ?? FilterMatchMode.CONTAINS,
            meins: 'meins' in filters ? filters.meins?.value ?? null : activeFilters?.meins ?? null,
            meinsMatchMode: 'meins' in filters ? filters.meins?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.meinsMatchMode ?? FilterMatchMode.CONTAINS,
        };

        if (hasYurticiData) {
            updatedFilters.yurticiSipMkt = 'yurticiSipMkt' in filters ? filters.yurticiSipMkt?.value ?? null : activeFilters?.yurticiSipMkt ?? null;
            updatedFilters.yurticiSipMktMatchMode = 'yurticiSipMkt' in filters ? filters.yurticiSipMkt?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.yurticiSipMktMatchMode ?? FilterMatchMode.CONTAINS;
        }

        if (hasYurtdisiData) {
            updatedFilters.yurtdisiSipMkt = 'yurtdisiSipMkt' in filters ? filters.yurtdisiSipMkt?.value ?? null : activeFilters?.yurtdisiSipMkt ?? null;
            updatedFilters.yurtdisiSipMktMatchMode = 'yurtdisiSipMkt' in filters ? filters.yurtdisiSipMkt?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.yurtdisiSipMktMatchMode ?? FilterMatchMode.CONTAINS;
        }

        onFiltersChange?.(updatedFilters);
    };

    const makeTextColumn = (field, headerKey, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '180px' } = options;

        return {
            field,
            columnKey: field,
            sortable,
            header: th(headerKey),
            style: { minWidth },
            body,
            bodyClassName: 'text-center',
            headerClassName: 'text-center',
            filter: true,
            filterField,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const columns = [
        makeTextColumn('matnr', 'laborCatalogReport.columns.laborCode', { minWidth: '150px' }),
        makeTextColumn('laborDescription', 'laborCatalogReport.columns.laborDescription', {
            minWidth: '300px',
            body: (row) => createTruncatedTextForTable(row?.laborDescription ? String(row.laborDescription) : '-', 30)
        }),
        makeTextColumn('meins', 'laborCatalogReport.columns.unit', { minWidth: '100px' }),
        ...(hasYurticiData ? [makeTextColumn('yurticiSipMkt', 'laborCatalogReport.columns.domesticTime', { minWidth: '150px', body: (row) => formatDecimal(row?.yurticiSipMkt) })] : []),
        ...(hasYurtdisiData ? [makeTextColumn('yurtdisiSipMkt', 'laborCatalogReport.columns.foreignTime', { minWidth: '150px', body: (row) => formatDecimal(row?.yurtdisiSipMkt) })] : [])
    ];

    return (
        <StickyScrollTable deps={[rows, hasYurticiData, hasYurtdisiData, activeFilters]} scrollBarPosition="top">
        <PreferencesDataTable
            tableKey="labor-catalog-report"
            columns={columns}
            value={rows}
            dataKey="_rowId"
            lazy
            paginator
            rows={lazyParams.rows}
            first={lazyParams.first}
            totalRecords={totalCount}
            onPage={onPage}
            loading={loading}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 25, 50]}
            showGridlines
            tableStyle={{ minWidth: '50rem' }}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
        />
        </StickyScrollTable>
    );
}